import React, { useMemo } from 'react';
import { isOverdue } from './MilestoneTracker';

const StatCard = ({ label, value, sub, color = 'text-white' }) => (
  <div className="p-4 rounded-2xl border border-white/8 bg-white/[0.04] space-y-1">
    <p className={`text-2xl font-black ${color}`}>{value}</p>
    <p className="text-[9px] font-black uppercase tracking-widest text-zinc-600">{label}</p>
    {sub && <p className="text-[9px] font-bold text-zinc-700">{sub}</p>}
  </div>
);

const MilestoneAnalytics = ({ milestones }) => {
  const stats = useMemo(() => {
    const total = milestones.length;
    const done = milestones.filter(m => m.completed || m.status === 'done').length;
    const inProg = milestones.filter(m => m.status === 'in_progress' && !m.completed).length;
    const review = milestones.filter(m => m.status === 'review' && !m.completed).length;
    const todo = milestones.filter(m => (m.status === 'todo' || !m.status) && !m.completed).length;
    const overdue = milestones.filter(isOverdue).length;
    const highPriority = milestones.filter(m => m.priority === 'high' && !m.completed).length;

    const byCategory = {};
    milestones.forEach(m => {
      const cat = m.category || 'other';
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    });

    const byPriority = { high: 0, medium: 0, low: 0 };
    milestones.forEach(m => { byPriority[m.priority || 'medium']++; });

    const completionRate = total > 0 ? Math.round((done / total) * 100) : 0;

    return { total, done, inProg, review, todo, overdue, highPriority, byCategory, byPriority, completionRate };
  }, [milestones]);

  const CAT_COLORS = {
    release: 'bg-purple-500', promotion: 'bg-blue-500', finance: 'bg-green-500',
    legal: 'bg-orange-500', event: 'bg-pink-500', other: 'bg-zinc-600',
  };

  const maxCat = Math.max(...Object.values(stats.byCategory), 1);

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Completion Rate" value={`${stats.completionRate}%`}
          color={stats.completionRate === 100 ? 'text-green-400' : stats.completionRate > 50 ? 'text-brand-pink' : 'text-zinc-300'} />
        <StatCard label="Done" value={stats.done} color="text-green-400" sub={`of ${stats.total} total`} />
        <StatCard label="In Progress" value={stats.inProg} color="text-brand-pink" />
        <StatCard label="Overdue" value={stats.overdue} color={stats.overdue > 0 ? 'text-red-400' : 'text-zinc-600'} />
      </div>

      {/* Status breakdown */}
      <div className="p-4 rounded-2xl border border-white/8 bg-white/[0.04] space-y-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Status Breakdown</p>
        {[
          { label: 'Done',        value: stats.done,   color: 'bg-green-500',   text: 'text-green-400' },
          { label: 'In Progress', value: stats.inProg, color: 'bg-brand-pink',  text: 'text-brand-pink' },
          { label: 'Review',      value: stats.review, color: 'bg-amber-400',   text: 'text-amber-400' },
          { label: 'To Do',       value: stats.todo,   color: 'bg-zinc-600',    text: 'text-zinc-400' },
        ].map(({ label, value, color, text }) => (
          <div key={label} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-black uppercase tracking-widest ${text}`}>{label}</span>
              <span className="text-[10px] font-black text-zinc-500">{value} / {stats.total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full rounded-full ${color} transition-all duration-700`}
                style={{ width: `${stats.total > 0 ? (value / stats.total) * 100 : 0}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Priority breakdown */}
      <div className="p-4 rounded-2xl border border-white/8 bg-white/[0.04] space-y-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Priority Distribution</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'High',   value: stats.byPriority.high,   color: 'text-red-400',   bg: 'bg-red-400' },
            { label: 'Medium', value: stats.byPriority.medium, color: 'text-amber-400', bg: 'bg-amber-400' },
            { label: 'Low',    value: stats.byPriority.low,    color: 'text-zinc-500',  bg: 'bg-zinc-500' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className="text-center space-y-1">
              <div className={`text-xl font-black ${color}`}>{value}</div>
              <div className={`h-1 rounded-full ${bg} mx-auto`} style={{ width: `${stats.total > 0 ? (value / stats.total) * 100 : 0}%`, minWidth: '8px' }} />
              <div className="text-[9px] font-black uppercase tracking-widest text-zinc-600">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Category breakdown */}
      {Object.keys(stats.byCategory).length > 0 && (
        <div className="p-4 rounded-2xl border border-white/8 bg-white/[0.04] space-y-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">By Category</p>
          <div className="space-y-2">
            {Object.entries(stats.byCategory).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
              <div key={cat} className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${CAT_COLORS[cat] || 'bg-zinc-600'}`} />
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400 w-20">{cat}</span>
                <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className={`h-full rounded-full ${CAT_COLORS[cat] || 'bg-zinc-600'}`}
                    style={{ width: `${(count / maxCat) * 100}%` }} />
                </div>
                <span className="text-[10px] font-black text-zinc-500 w-4 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneAnalytics;